/**
 * Splits a flag description, written as light Markdown in the flag provider,
 * into paragraphs and bullet lists with bold and code spans. Anything else
 * stays plain text; nothing is ever read as HTML.
 */

export type InlineSpan =
  | { kind: 'text'; text: string }
  | { kind: 'bold'; text: string }
  | { kind: 'code'; text: string };

export type DescriptionBlock =
  | { kind: 'paragraph'; spans: InlineSpan[] }
  | { kind: 'list'; items: InlineSpan[][] };

const INLINE_PATTERN = /(\*\*[^*]+\*\*|`[^`]+`)/;
const BULLET_PATTERN = /^[-*]\s+/;

export const parseInline = (text: string): InlineSpan[] =>
  text
    .split(INLINE_PATTERN)
    .filter((part) => part.length > 0)
    .map((part): InlineSpan => {
      if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
        return { kind: 'bold', text: part.slice(2, -2) };
      }
      if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
        return { kind: 'code', text: part.slice(1, -1) };
      }
      return { kind: 'text', text: part };
    });

const joinLines = (lines: string[]): string =>
  lines.map((line) => line.trim()).join(' ');

const paragraph = (lines: string[]): DescriptionBlock => ({
  kind: 'paragraph',
  spans: parseInline(joinLines(lines)),
});

/** Lines from the first bullet on become a list; any before it, a paragraph. */
const parseBlock = (lines: string[]): DescriptionBlock[] => {
  const firstBullet = lines.findIndex((line) =>
    BULLET_PATTERN.test(line.trim())
  );
  if (firstBullet === -1) return [paragraph(lines)];

  const heading = lines.slice(0, firstBullet);
  const items = lines.slice(firstBullet).reduce<string[][]>((acc, line) => {
    if (BULLET_PATTERN.test(line.trim())) {
      return [...acc, [line.trim().replace(BULLET_PATTERN, '')]];
    }
    // A wrapped bullet continues on the next lines.
    const last = acc.at(-1) ?? [];
    return [...acc.slice(0, -1), [...last, line]];
  }, []);

  const list: DescriptionBlock = {
    kind: 'list',
    items: items.map((item) => parseInline(joinLines(item))),
  };
  return heading.length > 0 ? [paragraph(heading), list] : [list];
};

export const parseDescription = (description: string): DescriptionBlock[] =>
  description
    .split(/\n\s*\n/)
    .map((block) => block.split('\n').filter((line) => line.trim() !== ''))
    .filter((lines) => lines.length > 0)
    .flatMap(parseBlock);
